import React, { useEffect, useMemo, useState } from "react";
import { FruitifyContainer } from "../../../shared-components/Containers";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { SyncRequestsList } from "../components/SyncRequestsList";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { Add, CloudSync, Search } from "@mui/icons-material";
import { RequestAccessDialog } from "../components/RequestAccessDialog";
import { usePagination } from "../../../hooks/usePagination";

function WondeSync() {
  const [open, setOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const ApiClient = useApiClient();

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timeout);
  }, [search]);

  const filters = useMemo(
    () => ({
      status: syncStatus,
      search: debouncedSearch,
      refreshVersion,
    }),
    [syncStatus, debouncedSearch, refreshVersion],
  );

  const {
    options: syncRequests,
    page,
    pageInfo,
    loading,
    error,
    handlePageChange,
  } = usePagination(
    async ({ first, after, status, search: searchFilter }) => {
      const response = await ApiClient.wonde.getWondeSyncRequests({
        first,
        after,
        status,
        search: searchFilter,
      });
      const options = response.wondeSyncStatusOptions || [];
      setStatusOptions(options);
      setSyncStatus(
        (currentStatus) =>
          currentStatus || options.find((option) => option.isDefault)?.code,
      );
      return response.wondeSyncRequests;
    },
    20,
    filters,
  );

  const refreshPage = () => setRefreshVersion((version) => version + 1);
  const selectedStatus = statusOptions.find(
    (option) => option.code === syncStatus,
  );

  const getAndUpdateRequests = async ({ refresh = false } = {}) => {
    try {
      const response = await ApiClient.wonde.getAndUpdateRequests({ refresh });
      if (response.success) {
        refreshPage();
        if (refresh) {
          enqueueSnackbar("Successfully fetched from Wonde", {
            variant: "success",
            autoHideDuration: 3000,
          });
        }
      } else if (refresh) {
        enqueueSnackbar("Problem fetching from Wonde", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (requestError) {
      console.error("Failed to get sync requests", requestError);
      if (refresh) {
        enqueueSnackbar("Problem fetching from Wonde", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    }
  };

  return (
    <FruitifyContainer>
      <RequestAccessDialog
        open={open}
        setOpen={setOpen}
        onComplete={refreshPage}
      />
      <Card>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Typography variant="h3" fontWeight="bold">
            Wonde Integration
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpen(true)}
            >
              Request access
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudSync />}
              onClick={() => getAndUpdateRequests({ refresh: true })}
            >
              Check for updates
            </Button>
          </Stack>
        </Stack>
      </Card>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1}
        sx={{ mt: 2 }}
      >
        <TextField
          label="Search schools"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ width: { xs: "100%", md: 400 } }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />
        <ToggleButtonGroup
          sx={{ width: { xs: "100%", md: 720 } }}
          value={syncStatus}
          exclusive
          onChange={(_, newSyncStatus) => {
            if (newSyncStatus) setSyncStatus(newSyncStatus);
          }}
          aria-label="Sync Status"
        >
          {statusOptions.map(({ code, label, count }) => (
            <ToggleButton sx={{ flex: 1 }} value={code} key={code}>
              {label} ({count})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ mt: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress aria-label="Loading Wonde syncs" />
          </Box>
        )}
        {error && <Alert severity="error">Unable to load Wonde syncs.</Alert>}
        {!loading && !error && syncRequests.length === 0 && (
          <Typography color="text.secondary">No Wonde syncs found.</Typography>
        )}
        {!loading && !error && syncRequests.length > 0 && (
          <SyncRequestsList
            requests={syncRequests}
            needsAction={selectedStatus?.needsAction}
            inProgress={selectedStatus?.inProgress}
            completed={selectedStatus?.completed}
            onRequestsChanged={refreshPage}
            dataTestId={`${syncStatus?.replace("_", "-")}-requests-list`}
          />
        )}
      </Box>
      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          page={page}
          onChange={handlePageChange}
          count={pageInfo.hasNextPage ? page + 1 : page}
          color="primary"
        />
      </Box>
    </FruitifyContainer>
  );
}

export { WondeSync };
